import { createContext, useContext } from 'react';
import type {
  TournamentParticipant,
  StatusFilter,
  RoleFilter,
  SortField,
  SortOrder,
} from '@/components/ParticipantsTable';

// Types shared across tournament detail tabs

export interface Match {
  id: number;
  team1: string;
  team2: string;
  /**
   * Knockout placeholder strings (e.g. "Winner Group A", "Winner R16-1") for
   * matches whose teams haven't been determined yet. Backend populates these
   * when the corresponding team_id is null; both fields are null for fully-
   * resolved matches. Consumed by MatchPreviewCard to render the slot as a
   * non-interactive placeholder row in the matches list.
   */
  team1_placeholder?: string | null;
  team2_placeholder?: string | null;
  match_date: string;
  deadline?: string;
  status: string;
  score_team1?: number;
  score_team2?: number;
  outcome?: string;
  venue?: string;
  matchday?: number;
  stage?: string;
  team1_logo_url?: string;
  team2_logo_url?: string;
  /**
   * Live timing — populated only for matches that have been polled while live.
   * `elapsed_minutes` is capped at the half boundary (45 for 1H, 90 for 2H,
   * 120 for ET); stoppage minutes live separately in `elapsed_extra_minutes`
   * so the display renders "45+3'" rather than a fake "48'".
   * `elapsed_updated_at` anchors the client-side tick that advances the
   * displayed minute between 5-min polls.
   */
  elapsed_minutes?: number | null;
  elapsed_extra_minutes?: number | null;
  elapsed_updated_at?: string | null;
  /**
   * Outcome coefficients surfaced on predicted, non-finished matches by the
   * list endpoint. Absent for unpredicted or finished matches.
   */
  team1_win_coefficient?: number | null;
  draw_coefficient?: number | null;
  team2_win_coefficient?: number | null;
  coefficients_source?: string;
  user_prediction?: {
    predicted_score_team1: number;
    predicted_score_team2: number;
    predicted_outcome?: string;
    odds_score?: number | string | null;
    points_earned?: number;
    points_breakdown?: {
      score: number;
      result: number;
      total: number;
    };
  };
}

export interface Tournament {
  id: number;
  name: string;
  description?: string;
  league?: {
    name: string;
    logo_url?: string;
  };
  /** `tournaments.league_id` — drives the tournament → `/matches/:leagueId` cross-link. */
  leagueId?: number;
  leagueLogo?: string;
  leagueName?: string;
  leagueCode?: string;
  /**
   * Season string from `tournaments.season` / `leagues.season` (e.g. "2026").
   * Combined with `leagueCode` ("1" = FIFA World Cup) it identifies a specific
   * edition of the competition. Used by `injectKnockoutBracket` to decide
   * whether to inject the World Cup 2026 placeholder bracket template.
   */
  season?: string;
  isPublic?: boolean;
  is_public?: boolean;
  isActive: boolean;
  matchesCount?: number;
  participantsCount?: number;
  userStatus?: 'active' | 'blocked' | 'awaiting';
  userBlockReason?: string | null;
  holderId?: number;
  userRole?: string;
  includeQualificationStages?: boolean;
  /** ISO timestamp when the tournament was archived; null/undefined while active. */
  archivedAt?: string | null;
}

export interface LeaderboardEntry {
  position: number;
  id: number;
  user_id?: number;
  username: string;
  first_name: string;
  last_name: string;
  total_predictions: number;
  correct_predictions: number;
  exact_predictions: number;
  total_points: number;
  average_points: number;
}

export interface LeaderboardData {
  leaderboard: LeaderboardEntry[];
  user_position?: {
    position: number | null;
    total_points: number;
    position_change?: number;
  };
  stats?: {
    total_participants: number;
    total_predictions: number;
    accuracy_percentage: number;
  };
}

export interface ParticipantStats {
  total_participants: number;
  active_participants: number;
  blocked_participants: number;
  awaiting_participants: number;
  active_predictors: number;
  avg_predictions_per_user: number;
}

export interface TournamentDetailContextType {
  // Core data
  tournament: Tournament;
  tournamentId: string;
  refetchTournament: () => void;

  // Permissions
  userRole: string | undefined;
  canManageParticipants: boolean;
  canAccessSettings: boolean;
  canInvite: boolean;
  canChangeRoles: boolean;
  /** True when the tournament is archived — predictions/coefficients are read-only everywhere. */
  isArchived: boolean;

  // Invite modal state
  showInviteModal: boolean;
  setShowInviteModal: (show: boolean) => void;

  // Block modal state
  blockModalParticipant: TournamentParticipant | null;
  setBlockModalParticipant: (p: TournamentParticipant | null) => void;

  // Confirm action modal state
  confirmAction: { participant: TournamentParticipant; type: 'delete' | 'revoke' } | null;
  setConfirmAction: (a: { participant: TournamentParticipant; type: 'delete' | 'revoke' } | null) => void;

  // Participant actions
  actionLoading: number | null;
  handleBlockToggle: (participant: TournamentParticipant, reason: string) => void;
  handleRevokeInvite: (participant: TournamentParticipant) => void;
  handleDeleteParticipant: (participant: TournamentParticipant) => void;
  handleConfirmAction: () => void;
  handleRoleChange: (participant: TournamentParticipant, newRole: 'participant' | 'maintainer') => void;
  refetchParticipants: () => void;

  // Participants tab state
  statusFilter: StatusFilter;
  roleFilter: RoleFilter;
  sortBy: SortField;
  sortOrder: SortOrder;
  participantsPage: number;
  participantsRowsPerPage: number;
  handleSortChange: (field: SortField, order: SortOrder) => void;
  handleStatusFilterChange: (status: StatusFilter) => void;
  handleRoleFilterChange: (role: RoleFilter) => void;
  setParticipantsPage: (page: number) => void;
  setParticipantsRowsPerPage: (rows: number) => void;

  // Layout helpers
  isMobile: boolean;
}

export const TournamentDetailContext = createContext<TournamentDetailContextType | undefined>(undefined);

export function useTournamentDetailContext() {
  const ctx = useContext(TournamentDetailContext);
  if (!ctx) throw new Error('useTournamentDetailContext must be used within TournamentDetailLayout');
  return ctx;
}
